import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { honorairesService } from '@/api/honoraires'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Activity, CreditCard, DollarSign, Download, FileSpreadsheet, FileText } from 'lucide-react'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Receipt } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { columns } from './components/honoraires-columns'
import { HonorairesToolbar } from './components/honoraires-toolbar'

// Exported so the toolbar can import it
export interface HonorairesTableParams {
  page: number        // offset: 0, 10, 20…
  page_size: number
  search?: string
  status?: string
  min_amount?: number
  max_amount?: number
  date_min?: string
  date_max?: string
}

export function Honoraires() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isExporting, setIsExporting] = useState(false)

  const [params, setParams] = useState<HonorairesTableParams>({
    page: 0,
    page_size: 10,
  })

  // Convert offset-based page to 1-based for the API
  const apiPage = Math.floor(params.page / params.page_size) + 1

  const { data, isLoading } = useQuery({
    queryKey: ['honoraires', params],
    queryFn: () =>
      honorairesService.list({
        page: apiPage,
        page_size: params.page_size,
        search: params.search || undefined,
        status: params.status || undefined,
        min_amount: params.min_amount,
        max_amount: params.max_amount,
        date_min: params.date_min,
        date_max: params.date_max,
      }),
  })

  const handleExport = async (fmt: 'excel' | 'pdf') => {
    try {
      setIsExporting(true)
      const blob = await honorairesService.exportData({
        search: params.search || undefined,
        status: params.status || undefined,
        min_amount: params.min_amount,
        max_amount: params.max_amount,
        date_min: params.date_min,
        date_max: params.date_max,
        export: fmt,
      })

      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `honoraires_export.${fmt === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast({ title: t('export_success'), description: t('export_success_desc') })
    } catch {
      toast({ title: t('export_error'), description: t('export_error_desc'), variant: 'destructive' })
    } finally {
      setIsExporting(false)
    }
  }

  const totalAmount = data?.meta?.total_amount ?? 0
  const totalPaid = data?.meta?.total_paid ?? 0
  const totalRemaining = data?.meta?.total_remaining ?? 0

  const fmt = (amount: number) =>
    new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount)

  return (
    <Main>
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Receipt className="h-5 w-5" />
          </span>
        }
        title={t('honoraires')}
        description={t('honoraires_desc')}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={isExporting} variant="outline">
                <Download className="me-2 h-4 w-4" />
                {isExporting ? t('exporting') : t('export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('excel')}>
                <FileSpreadsheet className="me-2 h-4 w-4" />
                {t('export_excel')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="me-2 h-4 w-4" />
                {t('export_pdf')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <div className="-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0">
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('honoraires_total')}</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmt(totalAmount)}</div>
                <p className="text-xs text-muted-foreground">{t('honoraires_for_period')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('honoraires_paid')}</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{fmt(totalPaid)}</div>
                <p className="text-xs text-muted-foreground">{t('honoraires_collected')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('honoraires_remaining')}</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{fmt(totalRemaining)}</div>
                <p className="text-xs text-muted-foreground">{t('honoraires_outstanding')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          {isLoading ? (
            <DataTableSkeleton columns={7} rows={10} />
          ) : (
            <DataTable
              columns={columns}
              data={data?.results ?? []}
              pages={data?.total_pages ?? 1}
              params={params}
              setParams={setParams}
              ToolbarName={HonorairesToolbar}
              storageKey="honoraires-table"
            />
          )}
        </div>
      </div>
    </Main>
  )
}
